import type { ReactNode } from "react";
import { Link } from "@inertiajs/react";
import {
  ChevronLeft,
  ChevronRight,
  Sprout,
  Target,
  Coins,
  CalendarDays,
  ArrowUpRight,
} from "lucide-react";
import {
  ImpactDashboardTemplate,
  ImpactGrid,
  ImpactTile,
} from "@/portal/templates/ImpactDashboardTemplate";
import { ImpactHero, Mark } from "@/portal/components/ImpactHero";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { BarChart } from "@/charts/BarChart";
import { SankeyChart, type SankeyFlow } from "@/charts/SankeyChart";
import { formatMoney } from "@/lib/money";

interface Highlight {
  label: string;
  amountMinor?: number;
  count?: number;
  href: string;
}

interface DashboardProps {
  userName: string;
  periodLabel: string;
  projectsCount: number;
  goalsSupported: number;
  contributionsCount: number;
  monthTotalMinor: number;
  monthEmpty: boolean;
  daily: {
    labels: string[];
    amountMinor: number[];
    count: number[];
  };
  prevMonthHref: string;
  nextMonthHref: string | null;
  showNext: boolean;
  highlights: {
    topGrossingMonth: Highlight | null;
    topProject: Highlight | null;
    topGoal: Highlight | null;
    mostActiveMonth: Highlight | null;
  };
  allocationFlows: SankeyFlow[];
}

const fromMinor = (minor: number) => minor / 100;
const money = (minor: number, compact = false) =>
  formatMoney(fromMinor(minor), "GBP", { compact });

export default function DashboardIndex(props: DashboardProps) {
  const firstName = props.userName?.trim().split(/\s+/)[0] || "there";

  return (
    <ImpactDashboardTemplate
      title={`Welcome back, ${firstName}`}
      subtitle={
        <>Contribution activity allocated to your projects in {props.periodLabel}.</>
      }
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Dashboard" }]}
      hideTimeRange
      hero={
        <ImpactHero
          eyebrow={props.periodLabel}
          amount={fromMinor(props.monthTotalMinor)}
          narrative={
            <>
              contributed across{" "}
              <Mark>
                {props.projectsCount}{" "}
                {props.projectsCount === 1 ? "project" : "projects"}
              </Mark>{" "}
              you supply.
            </>
          }
          meta={`${props.contributionsCount.toLocaleString()} contributions in ${props.periodLabel}`}
        />
      }
    >
      <ImpactGrid>
        {/* ── KPI row ─────────────────────────────────────────────── */}
        <ImpactTile col={3} index={0}>
          <StatCard
            label="Projects"
            value={props.projectsCount.toLocaleString()}
            hint="you supply"
          />
        </ImpactTile>
        <ImpactTile col={3} index={1}>
          <StatCard
            label="Goals"
            value={props.goalsSupported.toLocaleString()}
            hint="across your projects"
          />
        </ImpactTile>
        <ImpactTile col={3} index={2}>
          <StatCard
            label="Contributions"
            value={props.contributionsCount.toLocaleString()}
            hint={props.periodLabel}
          />
        </ImpactTile>
        <ImpactTile col={3} index={3}>
          <StatCard
            label="Total contributed"
            value={money(props.monthTotalMinor, true)}
            hint={props.periodLabel}
          />
        </ImpactTile>

        {/* ── Activity overview (with month navigation) ───────────── */}
        <ImpactTile col={12} index={4}>
          <Card>
            <Card.Header
              title="Activity overview"
              description={`Daily contributions allocated to your projects — ${props.periodLabel}.`}
              action={
                <div className="flex items-center gap-1">
                  <Button asChild intent="ghost" size="sm" aria-label="Previous month">
                    <Link href={props.prevMonthHref}>
                      <ChevronLeft />
                    </Link>
                  </Button>
                  {props.showNext && props.nextMonthHref ? (
                    <Button asChild intent="ghost" size="sm" aria-label="Next month">
                      <Link href={props.nextMonthHref}>
                        <ChevronRight />
                      </Link>
                    </Button>
                  ) : (
                    <Button intent="ghost" size="sm" aria-label="Next month" disabled>
                      <ChevronRight />
                    </Button>
                  )}
                </div>
              }
            />
            <Card.Body>
              {props.monthEmpty ? (
                <EmptyState
                  icon={<CalendarDays />}
                  title="No contributions this period"
                  description={`Nothing was allocated to your projects in ${props.periodLabel}.`}
                />
              ) : (
                <BarChart
                  labels={props.daily.labels}
                  series={[
                    {
                      label: "Contributed",
                      data: props.daily.amountMinor.map(fromMinor),
                      focal: true,
                    },
                  ]}
                  formatValue={(v) => formatMoney(v, "GBP", { compact: true })}
                  height={320}
                />
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* ── Allocation flow (all-time, source → project) ────────── */}
        <ImpactTile col={12} index={5}>
          <Card>
            <Card.Header
              title="Allocation flow"
              description="Where your contributions come from and which projects they fund, all-time."
            />
            <Card.Body>
              {props.allocationFlows.length === 0 ? (
                <EmptyState
                  icon={<Sprout />}
                  title="No allocations to visualise yet"
                  description="Once contributions are allocated to your projects, the flow will appear here."
                />
              ) : (
                <SankeyChart flows={props.allocationFlows} height={320} />
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* ── Highlights (all-time) ───────────────────────────────── */}
        <ImpactTile col={12} index={6}>
          <Card>
            <Card.Header
              title="Highlights"
              description="Your biggest contributors to date."
            />
            <Card.Body className="p-0">
              <div className="grid grid-cols-1 divide-y divide-border-subtle sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 [&>*:not(:first-child)]:sm:border-l [&>*:not(:first-child)]:sm:border-border-subtle">
                <HighlightCell
                  icon={<Coins />}
                  label="Top grossing month"
                  value={props.highlights.topGrossingMonth?.label}
                  sub={
                    props.highlights.topGrossingMonth
                      ? money(props.highlights.topGrossingMonth.amountMinor!)
                      : undefined
                  }
                  href={props.highlights.topGrossingMonth?.href}
                  internal
                />
                <HighlightCell
                  icon={<Sprout />}
                  label="Top project"
                  value={props.highlights.topProject?.label}
                  sub={
                    props.highlights.topProject
                      ? money(props.highlights.topProject.amountMinor!)
                      : undefined
                  }
                  href={props.highlights.topProject?.href}
                />
                <HighlightCell
                  icon={<Target />}
                  label="Top goal"
                  value={props.highlights.topGoal?.label}
                  sub={
                    props.highlights.topGoal
                      ? money(props.highlights.topGoal.amountMinor!)
                      : undefined
                  }
                  href={props.highlights.topGoal?.href}
                />
                <HighlightCell
                  icon={<CalendarDays />}
                  label="Most active month"
                  value={props.highlights.mostActiveMonth?.label}
                  sub={
                    props.highlights.mostActiveMonth
                      ? `${props.highlights.mostActiveMonth.count!.toLocaleString()} contributions`
                      : undefined
                  }
                  href={props.highlights.mostActiveMonth?.href}
                  internal
                />
              </div>
            </Card.Body>
          </Card>
        </ImpactTile>
      </ImpactGrid>
    </ImpactDashboardTemplate>
  );
}

function HighlightCell({
  icon,
  label,
  value,
  sub,
  href,
  internal,
}: {
  icon: ReactNode;
  label: string;
  value?: string;
  sub?: string;
  href?: string;
  /** Internal Inertia route (month nav) vs a not-yet-ported Hotwire page. */
  internal?: boolean;
}) {
  const body = (
    <div className="flex items-start gap-3 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent-text [&_svg]:size-4">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wider text-fg-subtle">
          {label}
        </div>
        <div className="mt-0.5 truncate text-sm font-medium text-fg">
          {value ?? "n/a"}
          {href && value && (
            <ArrowUpRight className="ml-1 inline size-3.5 text-fg-subtle" />
          )}
        </div>
        {sub && <div className="mt-0.5 truncate text-xs text-fg-muted tabular">{sub}</div>}
      </div>
    </div>
  );

  if (!href || !value) return body;
  return internal ? (
    <Link href={href} className="block transition-colors hover:bg-surface-hover">
      {body}
    </Link>
  ) : (
    <a href={href} className="block transition-colors hover:bg-surface-hover">
      {body}
    </a>
  );
}
